import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

let cartFetchPromise = null;
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]);

async function fetchCart() {
  if (!cartFetchPromise) {
    cartFetchPromise = fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => response.json())
      .finally(() => {
        cartFetchPromise = null;
      });
  }

  return cartFetchPromise;
}

function parseHandleList(value) {
  return new Set(
    (value || '')
      .split(',')
      .map((handle) => handle.trim().toLowerCase())
      .filter(Boolean)
  );
}

function parseCountryList(value) {
  return new Set(
    (value || '')
      .split(',')
      .map((country) => country.trim().toUpperCase())
      .filter(Boolean)
  );
}

function getCartItemHandle(item) {
  return (item?.handle || item?.product_handle || '').toString().toLowerCase();
}

class SoksiFreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.render(Number(this.dataset.cartTotal || 0), undefined);
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
  }

  handleCartUpdate = async (event) => {
    if (event?.detail?.data?.didError) return;

    const eventCart = event?.detail?.resource;
    if (eventCart && typeof eventCart.total_price === 'number') {
      this.render(eventCart.total_price, eventCart.items);
      return;
    }

    try {
      const cart = await fetchCart();
      this.render(cart.total_price, cart.items);
    } catch (error) {
      console.error('Failed to update free shipping bar:', error);
    }
  };

  getThreshold(items) {
    const country = this.dataset.country || '';

    if (country === 'JP' || this.dataset.currency === 'JPY') {
      const localHandles = parseHandleList(this.dataset.jpLocalHandles);
      const handles =
        items?.length > 0
          ? items.map(getCartItemHandle).filter(Boolean)
          : Array.from(parseHandleList(this.dataset.cartItemHandles));
      const productHandle = (this.dataset.productHandle || '').toLowerCase();

      if (productHandle) {
        handles.push(productHandle);
      }

      const localOnly = handles.length > 0 && handles.every((handle) => localHandles.has(handle));

      return Number(localOnly ? this.dataset.thresholdJpLocal : this.dataset.thresholdJpBbi) || 0;
    }

    const localMarketThreshold = Number(this.dataset.thresholdLocalMarket || 0);
    if (localMarketThreshold > 0) return localMarketThreshold;

    if (country === 'US') {
      return Number(this.dataset.thresholdUs) || 0;
    }

    if (parseCountryList(this.dataset.standardThresholdCountries).has(country)) {
      return Number(this.dataset.thresholdDefault) || 0;
    }

    return Number(this.dataset.thresholdRestOfWorld) || 0;
  }

  render(totalPrice, items) {
    const threshold = this.getThreshold(items);
    if (!threshold) return;

    const safeTotal = Number.isFinite(totalPrice) ? totalPrice : 0;
    const remaining = Math.max(threshold - safeTotal, 0);
    const progress = Math.min(Math.max((safeTotal / threshold) * 100, 0), 100);
    const isUnlocked = remaining <= 0;
    const message = isUnlocked ? this.unlockedMessage : this.remainingTemplate.replace('[[amount]]', this.format(remaining));

    this.dataset.cartTotal = safeTotal.toString();
    this.classList.toggle('is-unlocked', isUnlocked);
    this.setAttribute('aria-label', message);

    const messageElement = this.querySelector('[data-shipping-message]');
    if (messageElement) messageElement.textContent = message;

    const fill = this.querySelector('[data-shipping-fill]');
    if (fill instanceof HTMLElement) fill.style.width = `${progress}%`;

    const progressbar = this.querySelector('[data-shipping-progressbar]');
    if (progressbar) {
      progressbar.setAttribute('aria-valuemax', threshold.toString());
      progressbar.setAttribute('aria-valuenow', Math.min(safeTotal, threshold).toString());
      progressbar.setAttribute('aria-label', message);
    }
  }

  format(value) {
    const currency = this.dataset.currency || 'EUR';
    const displayValue = ZERO_DECIMAL_CURRENCIES.has(currency) ? Math.round(value / 100) : value;

    return formatMoney(displayValue, this.dataset.moneyFormat || '{{ amount }}', currency);
  }

  get remainingTemplate() {
    return this.dataset.remainingTemplate || "You’re [[amount]] away from free shipping";
  }

  get unlockedMessage() {
    return this.dataset.unlockedMessage || "You’ve unlocked free shipping";
  }
}

if (!customElements.get('soksi-free-shipping-bar')) {
  customElements.define('soksi-free-shipping-bar', SoksiFreeShippingBar);
}
